import type { JSX } from "react";
import { useEffect } from "react";

import { useAuth } from "./common/auth/AuthProvider.js";
import { WelcomePage } from "./common/auth/WelcomePage.js";
import {
  ActiveMembershipProvider,
  useActiveMembership,
} from "./common/membership/ActiveMembership.js";
import { shellForRole } from "./common/membership/roles.js";
import { registerPush } from "./common/push/registerPush.js";
import { CompanyShell } from "./shells/CompanyShell.js";
import { ControllerShell } from "./shells/ControllerShell.js";
import { CustomerShell } from "./shells/CustomerShell.js";

export function App(): JSX.Element {
  const { status } = useAuth();
  const authenticated = status === "authenticated";

  useEffect(() => {
    // Erst nach der Anmeldung: der Token gehört einem Konto, und ohne Konto gibt es
    // nichts, worüber benachrichtigt werden könnte.
    if (authenticated) {
      void registerPush();
    }
  }, [authenticated]);

  if (status === "loading") {
    return <FullPageNote text="Einen Moment …" />;
  }

  if (!authenticated) {
    return <WelcomePage />;
  }

  return (
    <ActiveMembershipProvider enabled>
      <Shell />
    </ActiveMembershipProvider>
  );
}

/**
 * Die Rollenweiche. Sie folgt der **aktiven** Mitgliedschaft, nicht der Menge aller:
 * wer im Kino den Einlass macht und abends selbst bucht, soll zwischen beidem wechseln
 * können, statt in der Ansicht festzustecken, die zufällig zuerst kam.
 */
function Shell(): JSX.Element {
  const { isPending, me, active } = useActiveMembership();

  if (isPending) {
    return <FullPageNote text="Konto wird geladen …" />;
  }
  if (!me) {
    return <FullPageNote text="Das Konto ist gerade nicht abrufbar." />;
  }

  switch (shellForRole(active?.role ?? null)) {
    case "controller":
      return <ControllerShell />;
    case "company":
      return <CompanyShell />;
    default:
      return <CustomerShell />;
  }
}

function FullPageNote({ text }: { text: string }): JSX.Element {
  return (
    <div className="grid min-h-full place-items-center bg-paper px-4">
      <p className="text-[14px] text-ink-3">{text}</p>
    </div>
  );
}
